import Vue from 'vue';

import { getPrice, getAts } from '../../helpers/productData';

// Price cache.
const pricesCache = {};

export default {
    state() {
        return {
            // Widget configuration.
            options: {},

            // Contains loaded product data: inventory levels, pricing, etc.
            products: null,
        };
    },

    getters: {
    },

    mutations: {
        /**
         * Saves options to state.
         */
        SET_OPTIONS(state, options) {
            state.options = options;
        },

        /**
         * Saves product data.
         */
        SET_PRODUCTS(state, products) {
            state.products = products;
        },
    },

    actions: {
        /**
         * Saves options.
         */
        setEcommerceOptions({ commit }, options) {
            commit('SET_OPTIONS', options);
        },

        /**
         * Loads prices for components mentioned in the blueprint.
         */
        getPrices({ state, dispatch, commit }) {
            return new Promise((resolve) => {
                dispatch('log', {
                    level: 'info',
                    message: '... on web, getting availability and prices',
                });

                if (state.options.getProductData) {
                    state.options.getProductData([]).then((data) => {
                        if (data?.productData) {
                            // Convert to map.
                            const map = {};

                            data.productData.forEach((p) => {
                                map[p.productId] = p;
                            });

                            commit('SET_PRODUCTS', map);

                            dispatch('log', {
                                level: 'info',
                                message: `... got ${data.productData.length} product records`,
                            });

                            resolve();
                        } else {
                            resolve();
                        }
                    });
                } else {
                    dispatch('log', {
                        level: 'info',
                        message: '... no product data callback, falling back to customizer availability',
                    });

                    resolve();
                }
            });
        },

        /**
         * Gets a product/component price.
         */
        getPrice({ state }, component) {
            return getPrice(state, component);
        },

        /**
         * Gets a product/component available stock.
         */
        getAts({ rootState }, component) {
            return getAts(rootState, component, rootState.customizer.viewController);
        },

        /**
         * Attaches necessary state related methods to the controller and triggers the loading status.
         */
        loadController({ state, rootState, dispatch }, controller) {
            controller.on('setReactive.store', (v) => {
                Vue.set(v.target, v.key, v.value);

                return true;
            });

            controller.on('initializeAvailability.store', () => dispatch('getPrices', {
                controller,
            }).then((data) => {
                dispatch('log', {
                    level: 'info',
                    message: 'Initialize availability...',
                });

                return data;
            }));

            controller.on('error.store', (error) => {
                dispatch('log', {
                    level: 'info',
                    message: `Controller error: ${error && error.message ? error.message : 'N/A'}`,
                });
            });

            const attachInventory = (to) => {
                to.on('getInventory.store', (c) => {
                    const v = getAts(rootState, c, to);

                    return v;
                });

                to.on('getPrice.store', (c) => {
                    const p = getPrice(state, c);

                    return p;
                });

                // When a component gets preempted due to rules, try to select a new component that matches the business requirements.
                // The default logic always pick the first available component.
                to.on('pickAlternate.store', (placement, component) => {
                    if (placement?.code === 'POPHead') {
                        // Pick a new head while preserving lashes state.
                        const hasLashes = component.tags?.includes('lashes');

                        // eslint-disable-next-line no-restricted-syntax
                        for (const c of placement.components) {
                            // Disallow placeholders on the first pass.
                            if (c.placeholder) {
                                // eslint-disable-next-line no-continue
                                continue;
                            }

                            if (!to.isAvailable(c)) {
                                // eslint-disable-next-line no-continue
                                continue;
                            }

                            const lashes = c.tags?.includes('lashes');

                            if (lashes === hasLashes) {
                                return c;
                            }
                        }

                        // eslint-disable-next-line no-restricted-syntax
                        for (const c of placement.components) {
                            if (!to.isAvailable(c)) {
                                // eslint-disable-next-line no-continue
                                continue;
                            }

                            const lashes = c.tags?.includes('lashes');

                            if (lashes === hasLashes) {
                                return c;
                            }
                        }
                    }

                    return null;
                });
            };

            attachInventory(controller);

            controller.on('socketAttached', (socket) => {
                if (socket.to) {
                    attachInventory(socket.to);
                }
            });

            dispatch('log', {
                level: 'info',
                message: `Load controller from ${controller.resolveBlueprintUrl()}`,
            });

            controller.load();
        },

        /**
         * Calculates full state price.
         */
        getStatePrice(_, { controller, options }) {
            const finalOptions = {
                ...{
                    ignoreOptional: true,

                    fixedPlacements: ['Product'],
                },
                ...options,
            };

            const price = controller.statePrice(finalOptions);

            return price;
        },

        /**
         * Formats price on the server using current locale settings.
         */
        formatPrice({ state }, price) {
            if (pricesCache[price]) {
                return Promise.resolve(pricesCache[price]);
            }

            return state.options.formatPrice(price).then((v) => {
                pricesCache[price] = v;

                return v;
            });
        },
    },
};
